/**
 * PostHog Analytics Configuration and Integration
 *
 * Provides comprehensive product analytics, user tracking, and feature flags
 *
 * NOTE: PostHog is initialized via PostHogProvider in main.tsx
 */

import posthog from 'posthog-js';

// Re-export the usePostHog hook for components
export { usePostHog } from '@posthog/react';

const ANALYTICS_EXCLUSION_STORAGE_KEY = 'figgy_analytics_excluded';
const ANALYTICS_INTERNAL_QUERY_PARAM = 'figgy_internal';

const SYNTHETIC_USER_AGENT_PATTERN =
  /HeadlessChrome|PhantomJS|Lighthouse|PageSpeed|Googlebot|bingbot|facebookexternalhit|Slackbot|Twitterbot|Discordbot|PostmanRuntime|curl|wget/i;

type AnalyticsExclusionReason = 'internal' | 'synthetic' | 'non_production';

/**
 * Keeps internal, preview, and browser-automation traffic out of product analytics.
 *
 * Visiting any page once with ?figgy_internal=1 excludes that browser until
 * ?figgy_internal=0 is visited. This is intentionally device-local.
 */
export function getAnalyticsExclusionReason(): AnalyticsExclusionReason | null {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return null;
  }

  try {
    const params = new URLSearchParams(window.location.search);
    const internalOverride = params.get(ANALYTICS_INTERNAL_QUERY_PARAM);

    if (internalOverride === '1') {
      window.localStorage.setItem(ANALYTICS_EXCLUSION_STORAGE_KEY, '1');
    } else if (internalOverride === '0') {
      window.localStorage.removeItem(ANALYTICS_EXCLUSION_STORAGE_KEY);
    }

    if (window.localStorage.getItem(ANALYTICS_EXCLUSION_STORAGE_KEY) === '1') {
      return 'internal';
    }
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }

  const hostname = window.location.hostname;
  const isNonProduction =
    import.meta.env.VITE_POSTHOG_ENVIRONMENT === 'development' ||
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.endsWith('.vercel.app');

  if (isNonProduction) {
    return 'non_production';
  }

  if (navigator.webdriver || SYNTHETIC_USER_AGENT_PATTERN.test(navigator.userAgent)) {
    return 'synthetic';
  }

  return null;
}

export function shouldExcludeAnalytics(): boolean {
  return getAnalyticsExclusionReason() !== null;
}

type TrackedEventListener = (eventName: string, properties?: Record<string, unknown>) => void;

const trackedEventListeners = new Set<TrackedEventListener>();

/**
 * Subscribe to product events as they are tracked, independently of whether
 * PostHog actually delivers them.
 *
 * UI that reacts to a product event must not go dark when analytics is
 * suppressed: `before_send` (main.tsx) drops events for internal browsers,
 * preview deployments, and bots, and ad blockers drop the PostHog script
 * outright. Subscribing to PostHog's own capture pipeline would inherit all of
 * those failure modes.
 *
 * Returns an unsubscribe function.
 */
export function onTrackedEvent(listener: TrackedEventListener): () => void {
  trackedEventListeners.add(listener);
  return () => {
    trackedEventListeners.delete(listener);
  };
}

/**
 * Track custom events
 */
export function trackEvent(eventName: string, properties?: Record<string, any>): void {
  // In-app subscribers run first so behaviour never depends on delivery.
  trackedEventListeners.forEach((listener) => {
    try {
      listener(eventName, properties);
    } catch (error) {
      console.error('Tracked event listener error:', error);
    }
  });

  try {
    posthog.capture(eventName, {
      ...properties,
      timestamp: Date.now(),
      platform: 'web',
    });
  } catch (error) {
    console.error('PostHog tracking error:', error);
  }
}

/**
 * Identify user (call when user logs in)
 */
export function identifyUser(userId: string, properties?: Record<string, any>): void {
  try {
    posthog.identify(userId, {
      ...properties,
      platform: 'booking-platform',
      environment: import.meta.env.VITE_POSTHOG_ENVIRONMENT,
    });
  } catch (error) {
    console.error('PostHog identify error:', error);
  }
}

/**
 * Set user properties
 */
export function setUserProperties(properties: Record<string, any>): void {
  try {
    posthog.setPersonProperties(properties);
  } catch (error) {
    console.error('PostHog user properties error:', error);
  }
}

/**
 * Track page views (automatic with routing)
 */
export function trackPageView(pageName?: string, properties?: Record<string, any>): void {
  try {
    posthog.capture('$pageview', {
      $current_url: window.location.href,
      page_name: pageName,
      ...properties,
    });
  } catch (error) {
    console.error('PostHog page view error:', error);
  }
}

// subscription_started / subscription_cancelled events are fired server-side
// from supabase/functions/stripe-webhook via PostHog HTTP API (more reliable
// than client-side since webhook is the source of truth for billing state).

/**
 * Business-specific tracking events
 */
export const BusinessAnalytics = {
  // Booking funnel tracking
  bookingStarted: (businessId: string, entryPath: string) =>
    trackEvent('booking_started', {
      businessId,
      entry_path: entryPath,
      funnel_step: 'start',
    }),

  serviceSelected: (
    serviceId: string,
    businessId: string,
    price: number,
    duration: number
  ) =>
    trackEvent('service_selected', {
      serviceId,
      businessId,
      price,
      duration,
      funnel_step: 'service_selected',
    }),
  
  bookingStepCompleted: (step: string, serviceId: string, businessId: string) =>
    trackEvent('booking_step_completed', { step, serviceId, businessId, funnel_step: step }),
    
  bookingCompleted: (bookingId: string, amount: number, serviceId: string, businessId: string) =>
    trackEvent('booking_completed', { bookingId, amount, serviceId, businessId, funnel_step: 'complete' }),
    
  bookingCancelled: (bookingId: string, reason: string, serviceId: string, businessId: string) =>
    trackEvent('booking_cancelled', { bookingId, reason, serviceId, businessId }),

  // Payment tracking
  paymentStarted: (amount: number, paymentMethod: string) =>
    trackEvent('payment_started', { amount, paymentMethod }),
    
  paymentCompleted: (amount: number, paymentMethod: string, bookingId: string) =>
    trackEvent('payment_completed', { amount, paymentMethod, bookingId }),
    
  paymentFailed: (amount: number, paymentMethod: string, error: string) =>
    trackEvent('payment_failed', { amount, paymentMethod, error }),

  // Waitlist tracking
  waitlistJoined: (serviceId: string, businessId: string, requestedDate: string) =>
    trackEvent('waitlist_joined', { serviceId, businessId, requestedDate }),
    
  waitlistNotified: (waitlistEntryId: string, slotDate: string) =>
    trackEvent('waitlist_notified', { waitlistEntryId, slotDate }),
    
  waitlistConverted: (waitlistEntryId: string, bookingId: string) =>
    trackEvent('waitlist_converted', { waitlistEntryId, bookingId }),

  // Business management
  businessSetupStarted: () =>
    trackEvent('business_setup_started'),
    
  businessSetupCompleted: (businessId: string, plan: string) =>
    trackEvent('business_setup_completed', { businessId, plan }),
    
  serviceCreated: (serviceId: string, businessId: string, price: number) =>
    trackEvent('service_created', { serviceId, businessId, price }),

  // User engagement
  dashboardViewed: (businessId: string) =>
    trackEvent('dashboard_viewed', { businessId }),
    
  revenueAnalyticsViewed: (businessId: string) =>
    trackEvent('revenue_analytics_viewed', { businessId }),
    
  settingsChanged: (setting: string, value: any, businessId: string) =>
    trackEvent('settings_changed', { setting, value, businessId }),

  // Feature usage
  featureUsed: (feature: string, context?: Record<string, any>) =>
    trackEvent('feature_used', { feature, ...context }),
    
  errorEncountered: (errorType: string, errorMessage: string, context?: Record<string, any>) =>
    trackEvent('error_encountered', { errorType, errorMessage, ...context }),
};

/**
 * Feature flags
 */
export function getFeatureFlag(flagName: string, defaultValue: boolean = false): boolean {
  try {
    return posthog.isFeatureEnabled(flagName) ?? defaultValue;
  } catch (error) {
    console.error('PostHog feature flag error:', error);
    return defaultValue;
  }
}

/**
 * A/B testing
 */
export function getFeatureFlagVariant(flagName: string): string | boolean {
  try {
    return posthog.getFeatureFlag(flagName) ?? false;
  } catch (error) {
    console.error('PostHog feature flag variant error:', error);
    return false;
  }
}

/**
 * Reset user (call on logout)
 */
export function resetUser(): void {
  try {
    posthog.reset();
  } catch (error) {
    console.error('PostHog reset error:', error);
  }
}

/**
 * Opt out user from tracking
 */
export function optOut(): void {
  try {
    posthog.opt_out_capturing();
  } catch (error) {
    console.error('PostHog opt out error:', error);
  }
}

/**
 * Opt in user for tracking
 */
export function optIn(): void {
  try {
    posthog.opt_in_capturing();
  } catch (error) {
    console.error('PostHog opt in error:', error);
  }
}

// Export PostHog instance for advanced usage
export { posthog };
export default posthog;
